import { Guide } from '../../types/guide';

const guide: Guide = {
  slug: 'export-text-and-markdown-to-pdf',
  title: 'Export Text and Markdown to a Formatted PDF',
  description:
    'Turn plain text or Markdown into a clean PDF in your browser: headings, lists, coloured code, clickable links, bookmarks, headers, watermarks and more.',
  category: 'Text editor',
  publishedDate: '2026-11-03',
  affiliateCategory: 'writing',
  body: `
The [Text Editor](/text-editor) can save your text as a real PDF — generated right in your browser, with nothing uploaded. Open **Download → PDF document**, choose your options and click **Download PDF**.

## Write in Markdown, get a formatted document

When **Format Markdown** is on (it switches on automatically for Markdown text), this source:

\`\`\`markdown
# Project Update

The launch is **on track**. Details are on the [project page](https://example.com).

## Next steps
- Finish testing
- Publish release notes
\`\`\`

becomes a document with a large title, a section heading, bold text, a clickable link and a bulleted list. Supported: headings, **bold**, *italic*, \`inline code\`, links, bulleted, numbered and nested lists, task lists, block quotes, code blocks, tables and horizontal rules.

With it off, the PDF keeps your text exactly as typed — every line break and indentation preserved — which is right for logs, poems, transcripts and code.

## Features that make the PDF feel professional

- **Bookmarks:** your headings become a clickable outline in the PDF viewer's sidebar, nested by heading level. Long documents become easy to navigate.
- **Clickable links:** Markdown links and bare web addresses (\`https://…\`) are real links in the PDF.
- **Coloured code:** code blocks are syntax-highlighted. Name the language after the opening fence (\`\`\`\` \`\`\`python \`\`\`\`) for the best result, or let it auto-detect.
- **Aligned tables:** Markdown tables keep their columns lined up.
- **Header:** *Header with title & date* prints the file name and today's date at the top of every page.
- **Page numbers:** "Page 3 of 12" at the bottom of each page.

## More options

Open **More options** in the dialog for:

- **Margins** — *Narrow* fits more on a page, *Wide* leaves room for notes and binding.
- **Spacing** — *Compact* for dense reference material, *Relaxed* for easy reading or proof-reading.
- **Watermark** — faint diagonal text behind every page, such as \`DRAFT\`, \`CONFIDENTIAL\` or \`SAMPLE\`.
- **Colour code blocks** — turn off for a plain, print-friendly look.

Your choices are remembered for next time.

## Tips for great results

- **Page size:** *Letter* is standard in the US and Canada, *A4* almost everywhere else. The dialog picks the one for your region.
- **Font:** *Serif* reads well for long-form text, *Sans* for reports and notes, *Mono* for code or anything where columns must line up.
- **Start from a document:** convert a Word file with the [Converter](/), click **Open in Text Editor**, tidy it up, then export — a quick way to restyle a document.
- **Clean first:** run *Fix PDF line breaks* or *Smart quotes → straight* before exporting text that came from elsewhere.

## About special characters

The PDF uses the standard built-in PDF fonts, which cover English and Western European languages, including accents (é, ü, ñ), curly quotes and dashes. Characters outside that set — for example Chinese, Japanese, Arabic or emoji — can't be drawn and appear as "?". The dialog warns you **before** exporting and tells you exactly how many characters are affected.
`,
};

export default guide;
